/**
 * O modelo de DOMÍNIO do front para caso. Não é o contrato da API.
 *
 * A distinção é o ponto deste arquivo. O fio é `@anery/api-contracts`, em
 * inglês, gerado do OpenAPI que a própria API emite — nenhum campo nasce
 * aqui, e um campo que o back não entrega não tem como aparecer. O que este
 * módulo descreve é o que a TELA manipula: pt-BR, porque carrega conceito de
 * negócio, e com instante já parseado, porque texto de data não é domínio.
 *
 * A tradução entre os dois mora em `mapeadores.ts`, e só lá.
 *
 * Histórico que vale registrar: até a geração do contrato, este arquivo
 * descrevia uma API imaginária (`protocolo`, `operadora`,
 * `beneficiarioIniciais`, `prazoEm`, envelope `PaginaDeCasos`) e um
 * vocabulário de estado próprio. Nada disso existe. Os campos saíram da UI
 * em vez de virar placeholder — coluna vazia mente menos que coluna
 * inventada, mas coluna que some diz a verdade.
 */

import type {
  CapturePendencyItem,
  CapturePendencyReason,
  CapturePendencyState,
  CaseDetail,
  CaseExtractedField,
  CaseListItem,
  CaseTrigger,
  ImplementationClockReason,
  Modality,
  ExtractedField,
  ExtractionSubject,
  MailDirection,
  WaitKind,
  WaitOrigin,
} from '@anery/api-contracts';

import type { Instante } from '../../../shared/dominio/datas';
import type { Trilha } from '../../../shared/ui/tiposDaEsteira';
import {
  type OrigemDoCampo,
  ROTULOS_DE_ORIGEM,
  ROTULOS_DE_SITUACAO_DO_PRAZO,
  type SituacaoDoPrazo,
} from '../../../shared/triagem/tipos';

/** Só para quem lê: o tipo de origem já traduzido, reusado da triagem (mesmo token). */
export { ROTULOS_DE_ORIGEM };
export type { OrigemDoCampo };

/** Situação do relógio de RESPOSTA, calculada NO SERVIDOR com o limiar configurado; reusa o tipo e os rótulos da triagem. */
export type SituacaoDaResposta = SituacaoDoPrazo;

export const ROTULOS_DE_SITUACAO_DA_RESPOSTA: Record<SituacaoDaResposta, string> = {
  ...ROTULOS_DE_SITUACAO_DO_PRAZO,
  // No caso, "sem prazo" é dito por extenso: o ticket de origem não tinha prazo.
  None: 'Sem prazo de resposta',
};

/** De onde vem o prazo de resposta, escrito na tela: o do ticket de triagem, contado da chegada do e-mail. */
export const ORIGEM_DO_PRAZO_DE_RESPOSTA = 'contado da chegada do e-mail, copiado do ticket de triagem';

export type GatilhoDoCaso = CaseTrigger;

/** Rótulo do botão de cada gatilho. `Record` de propósito: gatilho novo na API quebra o `tsc` aqui. */
export const ROTULOS_DE_GATILHO: Record<GatilhoDoCaso, string> = {
  StartAssessment: 'Iniciar avaliação',
  IssueQuote: 'Emitir orçamento',
  SubmitToPayer: 'Entregar à operadora',
  Authorize: 'Registrar autorização',
  Decline: 'Registrar recusa',
  Close: 'Encerrar caso',
};

/** O que o diálogo diz que VAI acontecer: nenhum gatilho envia nada para fora, todos só REGISTRAM a transição. */
export const EFEITOS_DE_GATILHO: Record<GatilhoDoCaso, string> = {
  StartAssessment: 'Registra que a avaliação começou.',
  IssueQuote: 'Registra que o orçamento foi emitido.',
  SubmitToPayer:
    'Registra que o caso foi entregue à operadora e PARA o relógio de resposta, com o horário do servidor. Nada é enviado daqui.',
  Authorize:
    'Registra a autorização, com o horário do servidor, e INICIA o relógio de implantação: o prazo conta daqui, pelas horas da modalidade. Nada é enviado daqui.',
  Decline: 'Registra que a operadora recusou. Não inicia o relógio de implantação.',
  Close: 'Encerra o caso. Depois disso a modalidade não muda mais.',
};

export type ModalidadeDoCaso = Modality;

export const ROTULOS_DE_MODALIDADE: Record<ModalidadeDoCaso, string> = {
  AD: 'Assistência domiciliar (AD)',
  ID: 'Internação domiciliar (ID)',
};

export const MODALIDADES_DO_CASO = Object.keys(ROTULOS_DE_MODALIDADE) as readonly ModalidadeDoCaso[];

export function ehModalidadeDoCaso(valor: unknown): valor is ModalidadeDoCaso {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DE_MODALIDADE, valor);
}

export type RazaoDaImplantacao = ImplementationClockReason;

export const ROTULOS_DA_RAZAO_DA_IMPLANTACAO: Record<RazaoDaImplantacao, string> = {
  NotAuthorized: 'ainda não começou: o caso não foi autorizado',
  NoModality: 'sem prazo: modalidade não definida',
  ModalityWithoutDeadline: 'sem prazo: não há horas configuradas para esta modalidade',
  Running: 'em andamento',
};

export const ROTULOS_CURTOS_DA_RAZAO_DA_IMPLANTACAO: Record<RazaoDaImplantacao, string> = {
  NotAuthorized: 'Não iniciada',
  NoModality: 'Sem prazo: sem modalidade',
  ModalityWithoutDeadline: 'Sem prazo: sem horas configuradas',
  Running: 'Em andamento',
};

export function ehRazaoDaImplantacao(valor: unknown): valor is RazaoDaImplantacao {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DA_RAZAO_DA_IMPLANTACAO, valor);
}

/** De onde vem o prazo de implantação: conta da AUTORIZAÇÃO, não da chegada do e-mail. */
export const ORIGEM_DO_PRAZO_DE_IMPLANTACAO = 'contado da autorização, pelas horas da modalidade';

export { ehEstadoDoCaso, ESTADOS_DO_CASO, ROTULOS_DE_ESTADO, type EstadoDoCaso } from './estado';

/**
 * Espera por terceiro (`US-313`/`TP-048`): só `Payer` (derivada do estado *aguardando operadora*, `D1`) e `Family`
 * (ato humano: abrir e fechar) existem hoje. Fica em inglês de propósito, mesmo vocabulário do fio.
 */
export type TipoDeEspera = WaitKind;

export const ROTULOS_DO_TIPO_DE_ESPERA: Record<TipoDeEspera, string> = {
  Payer: 'Aguardando a operadora',
  Family: 'Aguardando a família',
};

/** De onde o intervalo vem: `State` é derivado da transição, `Manual` é o ato de abrir/fechar. */
export type OrigemDaEspera = WaitOrigin;

/** Um intervalo de espera, como a API entrega em `waits` do detalhe. `id` é `null` para a espera da operadora — ela não tem linha própria, é derivada. */
export interface EsperaDoCaso {
  readonly id: string | null;
  readonly tipo: TipoDeEspera;
  readonly origem: OrigemDaEspera;
  readonly inicio: Instante;
  /** `null` = ainda aberta. */
  readonly fim: Instante | null;
  readonly abertaPorNome: string | null;
  readonly fechadaPorNome: string | null;
}

/**
 * O resumo de esperas do Caso (`waiting`, `D4`/`D5`): total, de terceiro e nosso — o total NÃO é descontado, só o
 * tempo "nosso" já desconta a união dos intervalos. `null` quando a API ainda não manda o campo.
 */
export interface ResumoDeEsperas {
  /** Que tipos têm intervalo aberto agora. */
  readonly abertas: readonly TipoDeEspera[];
  readonly totalSegundos: number;
  readonly deTerceiroSegundos: number;
  readonly nossoSegundos: number;
  /** Quanto do relógio de RESPOSTA em curso caiu dentro de uma espera (`D6`) — o prazo gravado não muda. */
  readonly pausaDeRespostaSegundos: number;
  /** O mesmo, para o relógio de IMPLANTAÇÃO. */
  readonly pausaDeImplantacaoSegundos: number;
}

/**
 * `3_600` → `"1h"`; `5_400` → `"1h 30min"`; `0` → `"0min"`.
 *
 * Arredonda para baixo ao minuto — segundo não é unidade que a pessoa decide
 * nada em cima, e mostrá-lo sugeriria uma precisão que a tela não tem uso para.
 */
export function formatarDuracaoEmHorasEMinutos(segundos: number): string {
  const totalDeMinutos = Math.floor(Math.max(0, segundos) / 60);
  const horas = Math.floor(totalDeMinutos / 60);
  const minutos = totalDeMinutos % 60;
  if (horas === 0) return `${String(minutos)}min`;
  if (minutos === 0) return `${String(horas)}h`;
  return `${String(horas)}h ${String(minutos)}min`;
}

/**
 * Um evento da linha do tempo do Caso (`GET /casos/{id}/linha-do-tempo`, `S14`).
 *
 * `detalhe` chega com os TOKENS CRUS que o evento gravou no banco (allowlist do snapshot, `D9`) — não o vocabulário
 * do contrato. Quem traduz para texto é `rotuloDoEventoDaLinhaDoTempo`, em `../linhaDoTempo`.
 */
export interface EventoDaLinhaDoTempo {
  readonly tipo: string;
  readonly ocorreuEm: Instante;
  readonly atorTipo: string | null;
  /** `null` = rotina automática, nunca um nome inventado. */
  readonly atorNome: string | null;
  readonly detalhe: Readonly<Record<string, string | null>>;
}

/**
 * Uma linha da fila, como a tela usa.
 *
 * Cinco campos, porque são cinco os que a API entrega. `criadoEm` e
 * `atualizadoEm` já são `Instante`: o texto ISO morre na fronteira, no
 * mapeador, e nenhum componente volta a chamar `parseInstante`.
 */

/** O passo da vez de uma frente — o que a tela precisa para oferecer o gesto. */
export interface PassoDaVez {
  readonly ordem: number;
  readonly rotulo: string;
  readonly gesto: 'confirma' | 'aprova_envio' | 'decide' | 'automatico';
  readonly responsavel: string;
  /** O texto do botão, como a curadoria o escreveu. Nunca um template do front. */
  readonly botao: string;
  readonly acao: string;
  /** As CHAVES a conferir. O VALOR de cada uma nunca atravessa (restrição 6). */
  readonly campos: readonly string[];
  /**
   * O marco da fila da avaliadora (`TP-065`): no passo que aciona, a tela pede o nome da avaliadora; no do aceite, o
   * turno. `null` no passo comum.
   */
  readonly marco: 'avaliadora_acionada' | 'avaliadora_aceitou' | null;
}

/** Uma frente, como o cartão e o detalhe a leem (`TP-044`). */
export interface FrenteLida {
  readonly frente: Trilha;
  readonly rotulo: string;
  readonly estado: 'aberta' | 'concluida' | 'dispensada';
  readonly passosFeitos: number;
  readonly totalDePassos: number;
  readonly paradaDesde: Instante;
  readonly prazoEm: Instante | null;
  readonly motivoDaDispensa: string | null;
  /** Esta é a frente que está segurando o Caso. */
  readonly travando: boolean;
  /** O rótulo da frente que o passo da vez espera. Não é trava — anda sozinha. */
  readonly esperando: string | null;
  readonly porQueEspera: string | null;
  /**
   * `null` quando a frente já encerrou **ou** quando ela espera outra: oferecer
   * o gesto a quem não pode dá-lo é o jeito mais rápido de a tela perder
   * credibilidade.
   */
  readonly passo: PassoDaVez | null;
}

/** O que a tela sabe das frentes de um Caso (`TP-044`). */
export interface ResumoDasFrentes {
  readonly curado: boolean;
  readonly prontas: number;
  readonly total: number;
  readonly travada: Trilha | null;
  /** Nenhuma frente aberta pode andar: todas esperam outra. Não há culpado interno. */
  readonly semTravaInterna: boolean;
  readonly porta1Aberta: boolean;
  readonly porta2Aberta: boolean;
  readonly tudoDispensado: boolean;
  readonly frentes: readonly FrenteLida[];
}

export interface CasoDaFila {
  readonly id: string;
  readonly pacienteId: string;
  /** `null` no modo mock (zero PII, de propósito) — a tela cai para `pacienteId`. */
  readonly pacienteNome: string | null;
  /**
   * Token do fio, SEM estreitar para `EstadoDoCaso`.
   *
   * A `BR-006` (`F4`) manda o Caso com substatus fora do mapeamento APARECER
   * sinalizado, nunca sumir — e um tipo estreito obrigaria o mapeador a
   * rejeitar a resposta inteira por causa de um Caso. Quem traduz para texto é
   * `rotuloDoEstado`, que cai para o token cru quando não conhece.
   */
  readonly estado: string;
  readonly criadoEm: Instante;
  readonly atualizadoEm: Instante;
  /** `null` = o ticket de origem não tinha prazo: a tela não inventa data. */
  readonly prazoDeRespostaEm: Instante | null;
  /** Quando o caso foi entregue à operadora e o relógio de resposta parou; `null` = corre. */
  readonly respondidoEm: Instante | null;
  /** Estado do relógio de resposta, calculado no servidor. */
  readonly situacaoDaResposta: SituacaoDaResposta;
  /** `AD` ou `ID`, como uma pessoa do núcleo definiu; `null` = ainda não definida. */
  readonly modalidade: ModalidadeDoCaso | null;
  /** Só vale para `ID`. */
  readonly ventilacaoContinua: boolean;
  /** Instante do servidor; daqui conta o prazo de implantação. `null` = não autorizado. */
  readonly autorizadoEm: Instante | null;
  /** Horas aplicadas, copiadas na autorização; `null` = não há prazo. */
  readonly horasDeImplantacao: number | null;
  /** Vencimento da implantação; `null` = não há prazo (a razão diz por quê). */
  readonly prazoDeImplantacaoEm: Instante | null;
  /** Estado do relógio de implantação, calculado no servidor; `None` sem prazo. */
  readonly situacaoDaImplantacao: SituacaoDaResposta;
  /** Por que existe, ou não, o prazo de implantação. */
  readonly razaoDaImplantacao: RazaoDaImplantacao;
  /** O paciente nasceu do e-mail e não tem vínculo ativo com o IW: o nome pode ser só o primeiro nome (`TP-040`, `D10`). */
  readonly pacienteProvisorio: boolean;
  /**
   * Por que o vínculo com o IW ainda falta, quando falta (`TP-053`): homônimo, novo no IW, desfeito ou em conflito.
   * `null` = vinculado, ou falta sem motivo (o IW ainda não foi consultado).
   */
  readonly motivoDoVinculoIw: MotivoDaPendencia | null;
  /** Quantas pendências do cadastro estão abertas. */
  readonly pendenciasAbertas: number;
  /**
   * QUAIS estão abertas, em ordem de nível. É o que deixa o cartão dizer
   * "falta CPF" em vez de "3 pendências". Só o token do item — a tabela não
   * guarda o valor do dado, e a tela não o mostraria se guardasse.
   */
  readonly itensPendentes: readonly ItemDaPendencia[];
  /**
   * TODAS as linhas de pendência, abertas e resolvidas.
   *
   * Sem este número, "zero abertas" confunde duas coisas opostas: o Caso que
   * a varredura conferiu e estava completo, e o Caso que ninguém nunca
   * varreu. Mostrar o segundo como completo é afirmar o que não se sabe.
   */
  readonly totalDePendencias: number;

  /**
   * As cinco frentes da captação (`TP-044`).
   *
   * `null` = a leitura não as trouxe. `curado: false` dentro dele = a operadora
   * não tem curadoria, que é **coisa diferente** e a tela precisa dizer: trilha
   * vazia parece completa, e essa mentira já foi paga uma vez.
   */
  readonly frentes: ResumoDasFrentes | null;
  /**
   * A completude verificada (`TP-058`): a MESMA conta do painel "O que falta para completar" e da trava do passo.
   * `null` = a resposta não a trouxe (modo mock, sem banco): aí o cartão cai nas pendências do cadastro.
   */
  readonly completude: ResumoDaCompletude | null;
  readonly nucleoId: string | null;
  /** `null` = o Caso não tem núcleo; só a sessão transversal o enxerga. */
  readonly nucleoNome: string | null;
  /** `null` só no modo mock antigo; a API sempre manda. */
  readonly operadora: string | null;
  /**
   * Quando o Caso entrou no estado ATUAL. Âncora da permanência na etapa
   * operadora e base do tempo decorrido em `aguardando operadora` (`S4`).
   */
  readonly entrouNoEstadoEm: Instante | null;
  /** O caso tem dono: a pessoa que o levou à avaliação (`TP-041`). `false` = "sem dono". */
  readonly temDono: boolean;
  /** Nome de exibição do dono; `null` com dono = a pessoa saiu do cadastro. Nunca o id. */
  readonly donoNome: string | null;
  /** Esperas por terceiro (`TP-048`); `null` = a API ainda não manda `waiting`. */
  readonly espera: ResumoDeEsperas | null;
}

/** O resumo da completude verificada do Caso (`TP-058`). */
export interface ResumoDaCompletude {
  readonly nivelUmCompleto: boolean;
  readonly nivelDoisCompleto: boolean;
  readonly faltam: number;
  /** O corte dos níveis é provisório até a Anery confirmar (NR-150). */
  readonly nivelProvisorio: boolean;
  /** Quantos itens o cadastro do paciente exige. */
  readonly cadastroTotal: number;
  /** O rótulo de cada item do cadastro que ainda falta. */
  readonly cadastroPendente: readonly string[];
}

/** O caso aberto no detalhe: a linha da fila mais o que a API diz que ainda vale. */
export interface CasoDetalhado extends CasoDaFila {
  /** Gatilhos permitidos a partir do estado atual (a máquina de estados é do back). */
  readonly gatilhosDisponiveis: readonly GatilhoDoCaso[];
  /** O que falta (e o que já faltou) no cadastro do paciente; lista vazia = nada registrado. */
  readonly pendencias: readonly PendenciaDoCaso[];
  /** Os intervalos de espera do Caso (`TP-048`); lista vazia = nenhum registrado. */
  readonly esperas: readonly EsperaDoCaso[];
  /** Chegada do primeiro e-mail do ticket de triagem de origem do Caso, a âncora do prazo de resposta; `null` = sem e-mail de origem. */
  readonly emailRecebidoEm: Instante | null;
}

export type ItemDaPendencia = CapturePendencyItem;
export type EstadoDaPendencia = CapturePendencyState;
export type MotivoDaPendencia = CapturePendencyReason;

/** Uma pendência do cadastro, como a tela usa: nunca carrega o valor do dado, só o que falta e desde quando. */
export interface PendenciaDoCaso {
  readonly item: ItemDaPendencia;
  /** 1 = mínimo para abrir; 2 = completo para avançar. */
  readonly nivel: 1 | 2;
  readonly estado: EstadoDaPendencia;
  readonly motivo: MotivoDaPendencia | null;
  /** A PRIMEIRA vez que a falta foi vista; não muda quando a pendência reabre. */
  readonly detectadaEm: Instante;
  readonly resolvidaEm: Instante | null;
}

export const ROTULOS_DO_ITEM_DA_PENDENCIA: Record<ItemDaPendencia, string> = {
  Name: 'Nome do paciente',
  Hospital: 'Hospital',
  Cpf: 'CPF',
  Address: 'Endereço',
  IwLink: 'Vínculo com o cadastro do IW',
};

export const ROTULOS_DO_ESTADO_DA_PENDENCIA: Record<EstadoDaPendencia, string> = {
  Open: 'Falta',
  Resolved: 'Resolvida',
};

export const ROTULOS_DO_NIVEL_DA_PENDENCIA: Record<1 | 2, string> = {
  1: 'mínimo para abrir',
  2: 'completo para avançar',
};

/**
 * O texto de "por que o vínculo com o IW ainda falta" (`TP-053`). Com um candidato só, o vínculo é automático; estes
 * são os casos em que ele espera alguém.
 */
export const ROTULOS_DO_MOTIVO_DA_PENDENCIA: Record<MotivoDaPendencia, string> = {
  FoundInIw: 'O IW tem um cadastro com este CPF. A próxima rodada faz o vínculo.',
  LinkConflict: 'O cadastro do IW achado já está vinculado a outro paciente. Conferir antes de juntar.',
  Homonym: 'Homônimo: o IW achou mais de um paciente, ou um só pelo nome. Escolha qual é.',
  NewInIw: 'Novo no IW, requer cadastro. Se o paciente aparecer no IW, o vínculo é feito sozinho.',
  Unlinked: 'O vínculo foi desfeito. Escolha o paciente certo do IW.',
};

/**
 * A marca curta da lista e do quadro para o vínculo com o IW (`TP-053`, `D12`): diz o que fazer, sem abrir o Caso.
 * `null` = nada a marcar (o motivo não pede ninguém).
 */
export const MARCAS_DO_VINCULO_IW: Partial<Record<MotivoDaPendencia, string>> = {
  Homonym: 'Homônimo: escolher',
  NewInIw: 'Novo no IW, requer cadastro',
  Unlinked: 'Vínculo desfeito: escolher',
  LinkConflict: 'Vínculo em conflito: conferir',
};

/** Marca ao lado do nome quando não há vínculo ativo com o IW. */
export const ROTULO_DO_PACIENTE_PROVISORIO = 'do e-mail, sem vínculo no IW';

/** A marca do vínculo na lista e no quadro, quando o motivo pede alguém; `null` = o motivo não tem marca própria. */
export function marcaDoVinculoIw(caso: Pick<CasoDaFila, 'motivoDoVinculoIw'>): string | null {
  return caso.motivoDoVinculoIw === null ? null : (MARCAS_DO_VINCULO_IW[caso.motivoDoVinculoIw] ?? null);
}

/**
 * O dono do caso em texto (`TP-041`): o nome; "Sem dono" antes de alguém levar o caso à avaliação; e um rótulo
 * neutro quando há dono mas a pessoa saiu do cadastro. Nunca o identificador.
 */
export function rotuloDoDono(caso: Pick<CasoDaFila, 'temDono' | 'donoNome'>): string {
  if (!caso.temDono) {
    return 'Sem dono';
  }
  return caso.donoNome ?? 'Dono fora do cadastro';
}

export function ehItemDaPendencia(valor: unknown): valor is ItemDaPendencia {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DO_ITEM_DA_PENDENCIA, valor);
}

export function ehEstadoDaPendencia(valor: unknown): valor is EstadoDaPendencia {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DO_ESTADO_DA_PENDENCIA, valor);
}

export function ehMotivoDaPendencia(valor: unknown): valor is MotivoDaPendencia {
  return typeof valor === 'string' && Object.hasOwn(ROTULOS_DO_MOTIVO_DA_PENDENCIA, valor);
}

export type { CaseDetail, CaseListItem };

/**
 * Sentido de um e-mail da thread, no vocabulário do FIO — mesma razão de
 * `EstadoDoCaso` ficar em inglês: é o token que trafega.
 */
export type SentidoDoEmail = MailDirection;

/**
 * `SentidoDoEmail` → o que a pessoa lê.
 *
 * "Entrada"/"Saída", não "Recebido"/"Enviado": o estado do CASO já usa
 * "Recebido" (`CaseState.Received`) — um rótulo igual, em contexto diferente
 * na mesma tela, tornaria uma busca por texto ambígua (e o "Estado" do caso
 * já significa outra coisa). "Entrada"/"Saída" também é o par que
 * `app.email.direcao` já grava (`entrada`/`saida`), então nada é inventado.
 */
export const ROTULOS_DE_SENTIDO: Record<SentidoDoEmail, string> = {
  Inbound: 'Entrada',
  Outbound: 'Saída',
};

/**
 * Uma mensagem da thread de e-mail de um caso (`TP-023`, `T6`/`T7`).
 *
 * `assunto` pode ser nulo — mesma coluna nulável de `app.email.assunto` do
 * back, e a tela mostra "(sem assunto)" em vez de célula vazia.
 */
export interface MensagemDaThread {
  readonly ticketId: string;
  readonly emailId: string;
  readonly assunto: string | null;
  readonly sentido: SentidoDoEmail;
  readonly recebidoEm: Instante;
}

/**
 * De onde um campo foi lido — e-mail ou anexo (`campo_extraido.objeto_tipo`,
 * `TP-031`, `T6`/`T7`). Fica em inglês de propósito: é o token que trafega,
 * mesma razão de `EstadoDoCaso`/`SentidoDoEmail`.
 */
export type ObjetoDaLeitura = ExtractionSubject;

export const ROTULOS_DO_OBJETO: Record<ObjetoDaLeitura, string> = {
  Mail: 'E-mail',
  Attachment: 'Anexo',
};

/**
 * `campo_extraido.campo` → o que a pessoa lê. Mesmo vocabulário de rótulo que
 * `DetalheDaTriagem.tsx` já usa por campo, num `Record` — porque aqui o campo
 * é dado dinâmico (lista), não uma propriedade fixa por nome.
 */
export const ROTULOS_DE_CAMPO: Record<ExtractedField, string> = {
  Name: 'Paciente',
  TaxId: 'CPF',
  MemberNumber: 'Matrícula',
  Icd: 'CID',
  Payer: 'Operadora',
  Hospital: 'Hospital',
  RequestType: 'Tipo de solicitação',
  RequestedParameters: 'Parâmetros solicitados',
  Deadline: 'Prazo',
  Protocol: 'Protocolo',
  DateOfBirth: 'Data de nascimento',
  Guardian: 'Responsável',
  Phone: 'Telefone',
  Address: 'Endereço',
  Device: 'Dispositivo',
  Requester: 'Solicitante',
  InjunctionFlag: 'Liminar',
  DiagnosticHypothesis: 'HD',
  Frequency: 'Frequência',
  SenderDomain: 'Domínio de quem enviou',
  Modality: 'Modalidade',
};

/** Campo clínico (`cid`/`hd`) — mesma disciplina de `CampoClinico` da triagem: sem `valor` no tipo. */
const CAMPOS_CLINICOS = new Set<ExtractedField>(['Icd', 'DiagnosticHypothesis']);

export function ehCampoClinico(campo: ExtractedField): boolean {
  return CAMPOS_CLINICOS.has(campo);
}

/**
 * Um campo lido de um e-mail ou anexo do caso (`GET /casos/{id}/campos-extraidos`,
 * `TP-031`, `T6`/`T7`). Sem reconciliação entre leituras concorrentes do mesmo
 * campo (`D1` do tech-plan) — cada leitura chega separada, com a origem.
 *
 * `valor`/`valorHumano` continuam nulos mesmo para campo clínico com
 * `encontrado: true` — a API já garante isso (`D3`), este tipo não reforça
 * a distinção com um segundo formato (diferente de `CampoClinico` da
 * triagem): `ehCampoClinico(campo)` é quem decide a apresentação.
 */
export interface CampoExtraidoDoCaso {
  readonly ticketId: string;
  readonly objetoId: string;
  readonly objetoTipo: ObjetoDaLeitura;
  readonly rotuloDeOrigem: string | null;
  readonly campo: ExtractedField;
  readonly ordem: number;
  readonly encontrado: boolean;
  readonly valor: string | null;
  readonly valorHumano: string | null;
  readonly confianca: number | null;
  readonly limiar: number | null;
  readonly origem: OrigemDoCampo | null;
}

/** Só para quem lê: o tipo do fio que o mapeador consome. */
export type { CaseExtractedField };
